import {
  Laptop,
  Briefcase,
  Coffee,
  Plane,
  HeartPulse,
  Armchair,
  Film,
  UtensilsCrossed,
  Trophy,
  Shirt,
  Home,
  Tag,
  type LucideIcon,
} from "lucide-react";

export interface CategoryTheme {
  slug: string;
  name: string;
  iconName: string;
  Icon: LucideIcon;
  color: string;
  gradient: string;
  gradientFrom: string;
  gradientTo: string;
  badgeGradient: string;
  subtleBg: string;
  glowColor: string;
  description: string;
}

export const CATEGORY_THEMES: Record<string, CategoryTheme> = {
  technology: {
    slug: "technology",
    name: "Technology",
    iconName: "Laptop",
    Icon: Laptop,
    color: "#0072FF",
    gradient: "linear-gradient(135deg, #00A2FF 0%, #0072FF 50%, #245EF8 100%)",
    gradientFrom: "#00A2FF",
    gradientTo: "#245EF8",
    badgeGradient: "linear-gradient(135deg, #00A2FF 0%, #0072FF 100%)",
    subtleBg: "rgba(0, 114, 255, 0.12)",
    glowColor: "rgba(0, 114, 255, 0.25)",
    description: "Cutting-edge gadgets, artificial intelligence, software architecture, and futuristic computing.",
  },
  business: {
    slug: "business",
    name: "Business",
    iconName: "Briefcase",
    Icon: Briefcase,
    color: "#4F46E5",
    gradient: "linear-gradient(135deg, #4F46E5 0%, #6366F1 50%, #8B5CF6 100%)",
    gradientFrom: "#4F46E5",
    gradientTo: "#8B5CF6",
    badgeGradient: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
    subtleBg: "rgba(79, 70, 229, 0.12)",
    glowColor: "rgba(79, 70, 229, 0.25)",
    description: "Executive strategy, global markets, entrepreneurship, finance, and leadership insights.",
  },
  lifestyle: {
    slug: "lifestyle",
    name: "Lifestyle",
    iconName: "Coffee",
    Icon: Coffee,
    color: "#EC4899",
    gradient: "linear-gradient(135deg, #EC4899 0%, #D946EF 50%, #A855F7 100%)",
    gradientFrom: "#EC4899",
    gradientTo: "#A855F7",
    badgeGradient: "linear-gradient(135deg, #EC4899 0%, #D946EF 100%)",
    subtleBg: "rgba(236, 72, 153, 0.12)",
    glowColor: "rgba(236, 72, 153, 0.25)",
    description: "Curated modern living, mindful routines, personal growth, and contemporary culture.",
  },
  travel: {
    slug: "travel",
    name: "Travel",
    iconName: "Plane",
    Icon: Plane,
    color: "#0284C7",
    gradient: "linear-gradient(135deg, #0284C7 0%, #06B6D4 50%, #14B8A6 100%)",
    gradientFrom: "#0284C7",
    gradientTo: "#14B8A6",
    badgeGradient: "linear-gradient(135deg, #0284C7 0%, #06B6D4 100%)",
    subtleBg: "rgba(2, 132, 199, 0.12)",
    glowColor: "rgba(2, 132, 199, 0.25)",
    description: "Inspiring global itineraries, hidden destinations, travel photography, and nomad guides.",
  },
  health: {
    slug: "health",
    name: "Health",
    iconName: "HeartPulse",
    Icon: HeartPulse,
    color: "#059669",
    gradient: "linear-gradient(135deg, #059669 0%, #10B981 50%, #34D399 100%)",
    gradientFrom: "#059669",
    gradientTo: "#34D399",
    badgeGradient: "linear-gradient(135deg, #059669 0%, #10B981 100%)",
    subtleBg: "rgba(5, 150, 105, 0.12)",
    glowColor: "rgba(5, 150, 105, 0.25)",
    description: "Evidence-based wellness, mental balance, holistic nutrition, and fitness longevity.",
  },
  furniture: {
    slug: "furniture",
    name: "Furniture",
    iconName: "Armchair",
    Icon: Armchair,
    color: "#B45309",
    gradient: "linear-gradient(135deg, #B45309 0%, #D97706 50%, #EA580C 100%)",
    gradientFrom: "#B45309",
    gradientTo: "#EA580C",
    badgeGradient: "linear-gradient(135deg, #B45309 0%, #D97706 100%)",
    subtleBg: "rgba(180, 83, 9, 0.12)",
    glowColor: "rgba(180, 83, 9, 0.25)",
    description: "Architectural interiors, ergonomic craft, artisanal materials, and timeless spatial design.",
  },
  entertainment: {
    slug: "entertainment",
    name: "Entertainment",
    iconName: "Film",
    Icon: Film,
    color: "#8B5CF6",
    gradient: "linear-gradient(135deg, #8B5CF6 0%, #A855F7 50%, #E11D48 100%)",
    gradientFrom: "#8B5CF6",
    gradientTo: "#E11D48",
    badgeGradient: "linear-gradient(135deg, #8B5CF6 0%, #E11D48 100%)",
    subtleBg: "rgba(139, 92, 246, 0.12)",
    glowColor: "rgba(139, 92, 246, 0.25)",
    description: "Cinema critiques, streaming highlights, music revolutions, and pop-culture storytelling.",
  },
  food: {
    slug: "food",
    name: "Food",
    iconName: "UtensilsCrossed",
    Icon: UtensilsCrossed,
    color: "#EA580C",
    gradient: "linear-gradient(135deg, #EA580C 0%, #F97316 50%, #FBBF24 100%)",
    gradientFrom: "#EA580C",
    gradientTo: "#FBBF24",
    badgeGradient: "linear-gradient(135deg, #EA580C 0%, #F97316 100%)",
    subtleBg: "rgba(234, 88, 12, 0.12)",
    glowColor: "rgba(234, 88, 12, 0.25)",
    description: "Gourmet culinary discoveries, artisanal recipes, dining culture, and culinary arts.",
  },
  sports: {
    slug: "sports",
    name: "Sports",
    iconName: "Trophy",
    Icon: Trophy,
    color: "#DC2626",
    gradient: "linear-gradient(135deg, #DC2626 0%, #EF4444 50%, #F97316 100%)",
    gradientFrom: "#DC2626",
    gradientTo: "#F97316",
    badgeGradient: "linear-gradient(135deg, #DC2626 0%, #EF4444 100%)",
    subtleBg: "rgba(220, 38, 38, 0.12)",
    glowColor: "rgba(220, 38, 38, 0.25)",
    description: "Championship analysis, athlete profiles, tournament breakdowns, and kinetic energy.",
  },
  // Legacy aliases
  fashion: {
    slug: "fashion",
    name: "Fashion",
    iconName: "Shirt",
    Icon: Shirt,
    color: "#EC4899",
    gradient: "linear-gradient(135deg, #EC4899 0%, #D946EF 50%, #8B5CF6 100%)",
    gradientFrom: "#EC4899",
    gradientTo: "#8B5CF6",
    badgeGradient: "linear-gradient(135deg, #EC4899 0%, #D946EF 100%)",
    subtleBg: "rgba(236, 72, 153, 0.12)",
    glowColor: "rgba(236, 72, 153, 0.25)",
    description: "Runway trends, sustainable apparel, style curation, and haute couture.",
  },
  "home-decor": {
    slug: "home-decor",
    name: "Home Decor",
    iconName: "Home",
    Icon: Home,
    color: "#78716C",
    gradient: "linear-gradient(135deg, #78716C 0%, #A8A29E 50%, #D6D3D1 100%)",
    gradientFrom: "#78716C",
    gradientTo: "#D6D3D1",
    badgeGradient: "linear-gradient(135deg, #78716C 0%, #A8A29E 100%)",
    subtleBg: "rgba(120, 113, 108, 0.12)",
    glowColor: "rgba(120, 113, 108, 0.25)",
    description: "Interior design inspiration and home decoration ideas.",
  },
};

const DEFAULT_THEME: CategoryTheme = {
  slug: "general",
  name: "General",
  iconName: "Tag",
  Icon: Tag,
  color: "#5E38F4",
  gradient: "linear-gradient(135deg, #00A2FF 0%, #5E38F4 50%, #F72074 100%)",
  gradientFrom: "#00A2FF",
  gradientTo: "#F72074",
  badgeGradient: "linear-gradient(135deg, #4F30E0 0%, #8A2EE4 100%)",
  subtleBg: "rgba(94, 56, 244, 0.12)",
  glowColor: "rgba(94, 56, 244, 0.25)",
  description: "Curated stories and expert perspectives across multiple disciplines.",
};

/**
 * Resolves a category slug or name into its canonical design theme, icon, and gradient tokens.
 */
export function getCategoryTheme(slugOrName?: string | null): CategoryTheme {
  if (!slugOrName) return DEFAULT_THEME;

  const key = slugOrName
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-");

  // Exact match
  if (CATEGORY_THEMES[key]) {
    return CATEGORY_THEMES[key];
  }

  // Alias matches
  if (key === "tech") return CATEGORY_THEMES.technology;
  if (key === "biz") return CATEGORY_THEMES.business;
  if (key === "decor" || key === "home") return CATEGORY_THEMES["home-decor"];
  if (key === "interior" || key === "living") return CATEGORY_THEMES.furniture;
  if (key === "ent" || key === "movies" || key === "cinema") return CATEGORY_THEMES.entertainment;

  return DEFAULT_THEME;
}
